import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { clinicInfo } from '@/mocks/homeData';

/* ============================================================
   ⚠️ ÁREA EDITÁVEL — Dados da Dra. Nathália Posso Lima
   ============================================================ */

const doctorData = {
  name: 'Dra. Nathália Posso Lima',
  role: 'Ginecologista & Obstetra',
  crm: 'CRM-CE 14200 / RQE 8571',
  image: 'https://storage.readdy-site.link/project_files/ed85e06b-2b23-49e7-a477-ed0f88fc71a8/553e3dd5-ff0e-4818-bd38-28d0ef016405_compressed_Captura-de-tela-2026-07-22-152724.webp',
  bio: [
    'Mestre em Ensino na Saúde, especialista em Ginecologia e Obstetrícia com foco em cirurgia minimamente invasiva e ultrassonografia. Dedica sua prática à saúde integral da mulher, unindo excelência técnica e acolhimento genuíno.',
    'Atua na Femme Vie desde sua fundação, em 2019, e construiu uma carreira pautada pelo cuidado personalizado — cada paciente recebe atenção individual, tempo adequado de consulta e um plano terapêutico pensado exclusivamente para suas necessidades.',
    'A Dra. Nathália acredita que a consulta ginecológica pode — e deve — ser uma experiência de confiança e bem-estar. Por isso, seu consultório é um espaço onde a mulher se sente segura para falar sobre sua saúde sem pressa e sem julgamentos.',
  ],
  specialties: [
    'Ginecologia Geral',
    'Obstetrícia',
    'Ultrassonografia Ginecológica e Obstétrica',
    'Cirurgia Ginecológica Minimamente Invasiva',
    'Videohisteroscopia',
    'Colposcopia',
    'Inserção de DIU e Implanon',
    'Laser de Rejuvenescimento Vaginal',
  ],
  instagramPosts: [
    {
      image: 'https://static.readdy.ai/image/5ae0a0be7c3b05867c1dbd14bfaff312/8ea8a8442bdaec6601adac6bcb86839f.png',
      caption: 'As Dra. Nathália Posso Lima e Dra. Mayna Moura juntas na Femme Vie, cuidando da saúde feminina com excelência e dedicação.',
      link: 'https://www.instagram.com/p/DXfnrOujaM2/',
    },
    {
      image: 'https://static.readdy.ai/image/5ae0a0be7c3b05867c1dbd14bfaff312/c2b2850ae3f0e51e2aaaf8a03de78012.png',
      caption: 'Quem é Nathália? Ginecologista desde 2016, formada em medicina pela UECE, Mestre em Ensino na Saúde, mãe da Sofia e do Francisco, apaixonada pela profissão.',
      link: 'https://www.instagram.com/p/DJwnyVvx4Ma/',
    },
    {
      image: 'https://static.readdy.ai/image/5ae0a0be7c3b05867c1dbd14bfaff312/9526471fd0ffc18b61e49f800ca94ea3.png',
      caption: 'Emagrecer fica mais difícil depois dos 40? Isso não é coincidência. Entenda as mudanças hormonais e como cuidar da sua saúde nessa fase.',
      link: 'https://www.instagram.com/p/DRQF06-iRNW/',
    },
    {
      image: 'https://static.readdy.ai/image/5ae0a0be7c3b05867c1dbd14bfaff312/513363674a09101cdbfc160542b1722d.png',
      caption: 'Você já ouviu falar sobre clareamento íntimo? Conheça os tratamentos disponíveis na Femme Vie para cuidar da sua saúde íntima com segurança e resultados.',
      link: 'https://www.instagram.com/p/DQxMrV6kduq/',
    },
  ],
  instagram: 'https://www.instagram.com/dranathaliaposso/',
  whatsapp: clinicInfo.whatsapp,
};

const testimonials = [
  {
    name: 'Ana Clara M.',
    city: 'Fortaleza, CE',
    rating: 5,
    text: 'A Dra. Nathália é simplesmente incrível. Me senti acolhida desde o primeiro minuto da consulta. Ela tem uma paciência enorme para explicar tudo e me deixou super à vontade. Nunca mais troco de ginecologista!',
    procedure: 'Consulta Ginecológica',
  },
  {
    name: 'Juliana R.',
    city: 'Fortaleza, CE',
    rating: 5,
    text: 'Fiz meu pré-natal completo com ela e a experiência foi maravilhosa. Consulta sem pressa, atenção em cada detalhe, ultrassons feitos na hora com muita calma. Recomendo de olhos fechados para qualquer gestante!',
    procedure: 'Pré-natal Humanizado',
  },
  {
    name: 'Camila S.',
    city: 'Fortaleza, CE',
    rating: 5,
    text: 'Depois de anos evitando ginecologista por vergonha, encontrei na Dra. Nathália um acolhimento que me fez querer cuidar da minha saúde de verdade. Eternamente grata por essa experiência transformadora.',
    procedure: 'Saúde Íntima',
  },
  {
    name: 'Patrícia N.',
    city: 'Eusébio, CE',
    rating: 5,
    text: 'Agendei para inserção de DIU e fiquei impressionada com a delicadeza do procedimento. A Dra. Nathália explicou passo a passo e me tranquilizou o tempo todo. O ambiente da Femme Vie também é impecável.',
    procedure: 'Inserção de DIU',
  },
];

const faqItems = [
  {
    id: 1,
    question: 'Quais procedimentos a Dra. Nathália realiza?',
    answer: 'A Dra. Nathália realiza consultas ginecológicas e obstétricas, ultrassonografia, colposcopia, videohisteroscopia, inserção de DIU e Implanon, cirurgia ginecológica minimamente invasiva e laser de rejuvenescimento vaginal.',
  },
  {
    id: 2,
    question: 'Qual é a formação da Dra. Nathália?',
    answer: 'A Dra. Nathália Posso Lima é Mestre em Ensino na Saúde, especialista em Ginecologia e Obstetrícia, com foco em cirurgia minimamente invasiva e ultrassonografia. Possui CRM-CE 14200 e RQE 8571.',
  },
  {
    id: 3,
    question: 'Como agendar uma consulta com a Dra. Nathália?',
    answer: 'O agendamento pode ser feito pelo WhatsApp (85) 9 8429-3412 ou pelo telefone (85) 3045-8454. Nossa equipe está disponível de segunda a sexta, das 8h às 18h.',
  },
  {
    id: 4,
    question: 'A Dra. Nathália faz acompanhamento de pré-natal?',
    answer: 'Sim, o pré-natal é uma de suas especialidades. Ela oferece acompanhamento completo da gestação, com consultas regulares e exames de ultrassom realizados no próprio consultório.',
  },
];

function StarRating({ count }: { count: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg
          key={i}
          className={`w-3.5 h-3.5 ${i < count ? 'text-accent-400' : 'text-background-300'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

export default function DraNathalia() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '8%']);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50 relative">
      {/* Lilás gradient background — 30% opacity */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 0%, rgba(139,126,197,0.30) 0%, transparent 60%)',
        }}
      />

      <Navbar />
      <main className="relative z-10">
        {/* Hero Section */}
        <section ref={heroRef} className="relative pt-24 md:pt-28 pb-16 md:pb-24 bg-background-100/80 overflow-hidden">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Image with parallax */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
                className="relative"
              >
                <div className="relative rounded-lg overflow-hidden aspect-[4/5] max-w-[480px] mx-auto lg:mx-0">
                  <motion.img
                    style={{ y: imageY }}
                    src={doctorData.image}
                    alt={doctorData.name}
                    className="w-full h-[115%] object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />
                </div>
                <div className="absolute -bottom-6 -right-6 w-40 h-40 rounded-full bg-primary-50/60 hidden lg:block" />
              </motion.div>

              {/* Info */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <span className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label">
                  Nossa Equipe
                </span>
                <h1 className="font-heading text-4xl md:text-6xl text-foreground-950 mt-3 leading-[1.05]">
                  Dra. Nathália<br />
                  <span className="italic font-light">Posso Lima</span>
                </h1>
                <p className="text-xs tracking-[0.08em] uppercase text-primary-500 mt-3 font-label">
                  {doctorData.role}
                </p>
                <p className="text-xs text-foreground-400 mt-1 font-label">
                  {doctorData.crm}
                </p>

                {/* Bio */}
                <div className="mt-6 space-y-4">
                  {doctorData.bio.map((paragraph, i) => (
                    <motion.p
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + i * 0.12, duration: 0.5 }}
                      className="text-sm text-foreground-600 leading-relaxed"
                    >
                      {paragraph}
                    </motion.p>
                  ))}
                </div>

                {/* CTA Buttons */}
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <motion.a
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    href={`https://wa.me/${doctorData.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm tracking-[0.06em] uppercase bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-300 whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line text-lg"></i>
                    Agendar Consulta
                  </motion.a>
                  <motion.a
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    href={doctorData.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-sm tracking-[0.06em] uppercase border border-background-300/60 text-foreground-700 hover:border-primary-300 hover:text-primary-500 transition-colors duration-300 whitespace-nowrap"
                  >
                    <i className="ri-instagram-line text-lg"></i>
                    Instagram
                  </motion.a>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Specialties Section */}
        <section className="relative py-20 md:py-28">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <div className="max-w-[900px] mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6 }}
                className="text-center mb-12"
              >
                <span className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label">Áreas de Atuação</span>
                <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-2">
                  Especialidades &amp; <span className="italic font-light">Procedimentos</span>
                </h2>
              </motion.div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {doctorData.specialties.map((spec, index) => (
                  <motion.div
                    key={spec}
                    initial={{ opacity: 0, x: -15 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.4, delay: 0.05 * index }}
                    whileHover={{ x: 4 }}
                    className="flex items-center gap-3 px-5 py-4 rounded-lg bg-background-100/80 border border-background-200/70 hover:border-primary-200/50 hover:shadow-[0_6px_20px_-10px_rgba(0,0,0,0.04)] transition-all duration-300"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-check-line text-sm"></i>
                    </div>
                    <span className="text-sm text-foreground-700 font-label">{spec}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section — REAL PATIENTS */}
        <section className="relative py-20 md:py-28 bg-background-100/60">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <span className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label">Depoimentos</span>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-2">
                O que dizem as <span className="italic font-light">pacientes</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-[1000px] mx-auto">
              {testimonials.map((t, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.5, delay: 0.08 * index }}
                  whileHover={{ y: -4 }}
                  className="group bg-background-50/90 rounded-xl p-6 border border-background-200/60 hover:border-primary-200/40 hover:shadow-[0_12px_40px_-15px_rgba(139,126,197,0.12)] transition-all duration-500"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-label">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-label text-foreground-800">{t.name}</p>
                      <p className="text-[11px] text-foreground-400">{t.city}</p>
                    </div>
                  </div>
                  <StarRating count={t.rating} />
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed italic">
                    &ldquo;{t.text}&rdquo;
                  </p>
                  <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-50 text-[11px] text-primary-600 font-label">
                    <i className="ri-heart-pulse-line text-[10px]"></i>
                    {t.procedure}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Instagram Posts Grid */}
        <section className="relative py-20 md:py-28">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <span className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label">Publicações Recentes</span>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-2">
                Acompanhe no <span className="italic font-light">Instagram</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {doctorData.instagramPosts.map((post, index) => (
                <motion.a
                  key={index}
                  href={post.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.5, delay: 0.08 * index }}
                  whileHover={{ y: -6 }}
                  className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:shadow-[0_20px_50px_-20px_rgba(0,0,0,0.07)] transition-all duration-500"
                >
                  <div className="aspect-square overflow-hidden relative">
                    <img
                      src={post.image}
                      alt={post.caption.slice(0, 80)}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-all duration-500">
                      <span className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 text-foreground-950 opacity-0 group-hover:opacity-100 scale-75 group-hover:scale-100 transition-all duration-500">
                        <i className="ri-instagram-line text-xl"></i>
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-foreground-600 leading-relaxed line-clamp-3">
                      {post.caption}
                    </p>
                    <span className="inline-flex items-center gap-1 mt-2 text-[11px] text-primary-500 font-label whitespace-nowrap group-hover:gap-1.5 transition-all duration-300">
                      <i className="ri-instagram-line"></i>
                      Ver no Instagram
                      <i className="ri-arrow-right-line text-[10px] opacity-0 group-hover:opacity-100 transition-all duration-300"></i>
                    </span>
                  </div>
                </motion.a>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="relative py-20 md:py-28 bg-background-100/60">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
            <div className="max-w-[800px] mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6 }}
                className="text-center mb-12"
              >
                <span className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label">Perguntas Frequentes</span>
                <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-2">
                  Tire suas <span className="italic font-light">dúvidas</span>
                </h2>
              </motion.div>

              <div className="space-y-3">
                {faqItems.map((item, index) => (
                  <motion.details
                    key={item.id}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.4, delay: 0.06 * index }}
                    className="group bg-background-50/90 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200/50 transition-all duration-300 open:border-primary-200/60 open:shadow-[0_8px_30px_-12px_rgba(0,0,0,0.04)]"
                  >
                    <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                      <span className="text-sm font-label text-foreground-800 pr-4">{item.question}</span>
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 group-open:bg-primary-50 group-open:text-primary-500 transition-all duration-300">
                        <i className="ri-add-line text-sm group-open:hidden"></i>
                        <i className="ri-subtract-line text-sm hidden group-open:block"></i>
                      </span>
                    </summary>
                    <div className="px-6 pb-4">
                      <div className="h-[1px] bg-background-200/60 mb-4" />
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                    </div>
                  </motion.details>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(ellipse at center, rgba(139,126,197,0.15), transparent 60%)',
            }}
          />
          <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 text-center relative z-10">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6 }}
              className="font-heading text-3xl md:text-4xl text-foreground-950"
            >
              Agende sua consulta com a<br />
              <span className="italic font-light">Dra. Nathália</span>
            </motion.h2>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3"
            >
              <motion.a
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                href={`https://wa.me/${doctorData.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-sm tracking-[0.06em] uppercase bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-300 whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                Agendar pelo WhatsApp
              </motion.a>
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
                <Link
                  to="/contato"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-sm tracking-[0.06em] uppercase border border-background-300/60 text-foreground-700 hover:border-primary-300 hover:text-primary-500 transition-colors duration-300 whitespace-nowrap"
                >
                  Enviar Mensagem
                  <i className="ri-mail-line"></i>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}